import { Icon, Typography } from '@smartgift/frontend-toolkit';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import React, { useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { configs } from '@hero/configs';
import { VALIDATION_ERRORS } from '@hero/constants';
import { trimStringsInObject } from '@hero/utils';
import { isEmailValid } from '@hero/utils/validationUtils';

import { SpreadSheetEmailTable } from '../../Table/Email/SpreadSheetEmailTable';
import { UPLOAD_EMAIL_STEPS } from '../../UploadEmailSpreadSheet/constant';

import { FileUploadArea } from './FileUploadArea';
import * as S from './style';

const csvHeader = ['email'];

function UploadEmailSpreadSheet({
  setUploadedEmails,
  onRequestClose,
  uploadedEmails,
  setFooterProps,
  setStep
}) {
  const { t } = useTranslation();

  const handleFile = async file => {
    await new Promise(resolve => {
      let message = '';
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = async ({ target: { result } }) => {
        const XLSX = await import('xlsx');
        const wb = XLSX.read(result, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];

        const range = XLSX.utils.decode_range(ws['!ref']);
        range.s.r = 1; // <-- zero-indexed, so setting to 1 will skip row 0
        ws['!ref'] = XLSX.utils.encode_range(range);

        const emails = XLSX.utils.sheet_to_json(ws, {
          header: csvHeader,
          raw: true,
          rawNumbers: false
        });

        const uniqEmails = uniqBy(emails, 'email');
        const duplicateEmailCount = emails.length - uniqEmails.length;

        if (uniqEmails.length) {
          message = t('contactsSuccessfullyAdded', { count: uniqEmails.length });
        } else {
          message = t('noContactsAreAdded');
        }

        toast(
          {
            title: message,
            message: t('addMoreContactsOrContinue')
          },
          {
            className: 'toast-wrapper success'
          }
        );

        if (uniqEmails.length && duplicateEmailCount > 0) {
          toast(
            {
              message: t('duplicateEmailRemoved', { count: duplicateEmailCount })
            },
            {
              className: 'toast-wrapper warning'
            }
          );
        }

        setUploadedEmails(oldUploadedEmails => [
          ...oldUploadedEmails,
          ...sortBy(
            uniqEmails.map((email, key) => {
              const trimEmail = trimStringsInObject(email);

              return {
                id: key,
                hasError: !isEmailValid(trimEmail.email),
                email: trimEmail.email
              };
            }),
            [a => !a.hasError]
          )
        ]);

        resolve();
      };
    });
  };

  const { addModalError, reset } = useValidationErrorsStore();

  const hasErrorEmails = useMemo(() => {
    const hasError = uploadedEmails.filter(r => r.hasError);
    if (!hasError.length) {
      reset();
    }
    return hasError;
  }, [uploadedEmails]);

  useEffect(() => {
    setFooterProps({
      leftButtonText: t('cancel'),
      leftButtonOnClick: () => onRequestClose(),
      isLeftButtonActive: true,
      isRightButtonActive: uploadedEmails.length > 0,
      rightButtonOnClick: async () => {
        if (hasErrorEmails.length) {
          await addModalError(
            <Trans
              i18nKey={VALIDATION_ERRORS.hasInvalidInviteEmail}
              components={{ strong: <strong /> }}
            />
          );
          return;
        }

        setStep(UPLOAD_EMAIL_STEPS.REVIEW_IMPORT);
      },
      rightButtonText: t('next'),
      rightButtonEndIcon: <Icon name="next" />
    });
  }, [uploadedEmails]);

  return isEmpty(uploadedEmails) ? (
    <FileUploadArea
      handleFile={handleFile}
      infoText={'dragAndDropFileInfoEmail'}
      csvFileUrl={`${configs.publicUrl}/assets/docs/sgb-email-validation-template.csv`}
      xlsFileUrl={`${configs.publicUrl}/assets/docs/sgb-email-validation-template.xlsx`}
    />
  ) : (
    <>
      <S.TitleHeader>
        <Typography variant="body1Bold" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {hasErrorEmails.length ? (
            <>
              <Icon name="f-error" />
              <Trans
                i18nKey="uploadSpreadSheetErrorEmail"
                values={{ errorEmailCount: hasErrorEmails.length }}
                components={{ b: <S.ErrorText /> }}
              />
            </>
          ) : (
            <>
              <Icon name="f-success" />
              {t('uploadSpreadSheetSuccessEmail', {
                totalEmailCount: uploadedEmails.length
              })}
            </>
          )}
        </Typography>
        {hasErrorEmails.length > 0 && (
          <Typography variant="body2" color="fg" emphasis="weaker">
            {t('uploadSpreadSheetDescriptionEmail')}
          </Typography>
        )}
      </S.TitleHeader>

      <SpreadSheetEmailTable emails={uploadedEmails} updateEmails={setUploadedEmails} />
    </>
  );
}

export { UploadEmailSpreadSheet };
